import {
  fetchDashboardExperience,
  fetchDashboardExperienceV2,
  type DashboardExperienceResponse,
  updateDashboardExperienceOrder,
  type UpdateDashboardExperienceOrderRequest,
} from '@library/api-dashboard-experience'
import React from 'react'
import { useSWRConfig } from 'swr'

import { useDashboardExperienceApiConfig } from './useDashboardExperienceApiConfig'

type UseUpdateDashboardOrderResult = {
  isUpdating: boolean
  updateOrder: (params: {
    orderId: string
    body: UpdateDashboardExperienceOrderRequest
  }) => Promise<DashboardExperienceResponse | undefined>
}

const useUpdateDashboardOrder = (): UseUpdateDashboardOrderResult => {
  const { mutate } = useSWRConfig()
  const [isUpdating, setIsUpdating] = React.useState(false)
  const { apiVersion, cacheKey: dashboardExperienceCacheKey } = useDashboardExperienceApiConfig()
  const fetchCurrentDashboardExperience =
    apiVersion === 'v2' ? fetchDashboardExperienceV2 : fetchDashboardExperience

  const updateOrder = React.useCallback(
    async ({ orderId, body }: { orderId: string; body: UpdateDashboardExperienceOrderRequest }) => {
      setIsUpdating(true)

      try {
        await updateDashboardExperienceOrder({ orderId, body })

        const refreshedDashboardExperience = await fetchCurrentDashboardExperience()

        await mutate(dashboardExperienceCacheKey, refreshedDashboardExperience, {
          populateCache: true,
          revalidate: false,
        })

        return refreshedDashboardExperience
      } finally {
        setIsUpdating(false)
      }
    },
    [dashboardExperienceCacheKey, fetchCurrentDashboardExperience, mutate],
  )

  return {
    isUpdating,
    updateOrder,
  }
}

export { useUpdateDashboardOrder }
